// ============================================================
// RightPanel — exactly three modes: detail | comms | action.
//
// v21 layout-density slice: when nothing is selected the panel no
// longer dead-ends at "No selection" — it renders Command Actions
// (Quick Create links to real routes, Recent Activity from the live
// receipts feed, Needs Attention from blocked jobs). Both feeds fail
// to honest empty states, never fake rows. Selecting a record still
// replaces all of it with the detail stack — the three panel modes
// stay exactly three.
// ============================================================

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import EmptyState from '../components/EmptyState'
import { useShell } from './ShellContext'
import { SHELL_TOKENS, text } from './styles'
import { apiRequest } from '../api/client'

export default function RightPanel() {
  const { panelMode, selected, clear } = useShell()

  const title = (() => {
    if (panelMode === 'comms') return 'Comms'
    if (panelMode === 'action') return 'Action'
    if (selected && typeof selected === 'object') {
      const s = selected as Record<string, unknown>
      return (
        (s.action as string) ||
        (s.event_type as string) ||
        (s.user_id as string) ||
        (s.name as string) ||
        'Detail'
      )
    }
    return 'Command Actions'
  })()

  return (
    <aside
      className="sc-shell-right"
      aria-label="Context panel"
      style={{
        borderLeft: `1px solid ${SHELL_TOKENS.borderAlt}`,
        background: 'rgba(8,8,12,0.72)',
        backdropFilter: 'blur(16px)',
        padding: '18px 16px',
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 16,
          paddingBottom: 12,
          borderBottom: `1px solid ${SHELL_TOKENS.border}`,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              ...text.kicker,
              fontSize: 8,
              letterSpacing: '0.22em',
              color: SHELL_TOKENS.inkWhisper,
              marginBottom: 6,
            }}
          >
            RIGHT PANEL · {panelMode.toUpperCase()}
          </div>
          <div
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: SHELL_TOKENS.ink,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </div>
        </div>
        <button
          type="button"
          onClick={clear}
          style={{
            border: `1px solid ${SHELL_TOKENS.border}`,
            background: SHELL_TOKENS.surfaceRaised,
            color: SHELL_TOKENS.inkDim,
            borderRadius: 6,
            padding: '4px 9px',
            cursor: 'pointer',
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: '0.10em',
            textTransform: 'uppercase',
            fontFamily: 'var(--font-mono)',
          }}
        >
          Close
        </button>
      </div>

      {panelMode === 'detail' && selected && <DetailStack selected={selected as Record<string, unknown>} />}

      {panelMode === 'detail' && !selected && <CommandActions />}

      {panelMode === 'comms' && (
        <EmptyState
          title="Communication lane"
          body="Use this mode for incident chat, assignment notes, or request-thread visibility."
        />
      )}

      {panelMode === 'action' && (
        <EmptyState
          title="Action lane"
          body="Use this mode for revoke, escalate, assign, verify, approve, or dispatch actions."
        />
      )}
    </aside>
  )
}

function DetailStack({ selected }: { selected: Record<string, unknown> }) {
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {Object.entries(selected).map(([k, v]) => (
        <div
          key={k}
          style={{
            border: `1px solid ${SHELL_TOKENS.border}`,
            background: SHELL_TOKENS.surface,
            borderRadius: 8,
            padding: '10px 12px',
          }}
        >
          <div
            style={{
              fontSize: 8,
              color: SHELL_TOKENS.inkWhisper,
              textTransform: 'uppercase',
              letterSpacing: '0.16em',
              marginBottom: 5,
              fontFamily: 'var(--font-mono)',
            }}
          >
            {k}
          </div>
          <div
            style={{
              fontSize: 11,
              color: SHELL_TOKENS.inkDim,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: 'var(--font-mono)',
            }}
          >
            {formatValue(v)}
          </div>
        </div>
      ))}
    </div>
  )
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'object') return JSON.stringify(v, null, 2)
  return String(v)
}

// ------------------------------------------------------------
// Command Actions — the no-selection default (v21). Quick Create
// links go to real routes; Recent Activity reads the live receipts
// feed; Needs Attention reads blocked jobs. Failed or empty feeds
// render honest empty lines — never fake rows.
// ------------------------------------------------------------
const QUICK_CREATE: Array<{ label: string; route: string }> = [
  { label: 'Secure Access', route: '/square/olivium/secure-access' },
  { label: 'Post Job', route: '/square/command-center/operations' },
  { label: 'Team Invite', route: '/square/onboarding/secure-invites' },
  { label: 'Daily Meeting', route: '/square/command-center/communications/conference-calls' },
  { label: 'Creative Brief', route: '/square/command-center/workspaces/creative' },
  { label: 'Calendar Plan', route: '/square/command-center/calendar' },
  { label: 'Agent Portal', route: '/square/command-center/agents' },
]

function sectionHeader(label: string) {
  return (
    <div
      style={{
        ...text.kicker,
        fontSize: 8.5,
        letterSpacing: '0.2em',
        color: SHELL_TOKENS.inkWhisper,
        margin: '14px 0 8px',
      }}
    >
      {label}
    </div>
  )
}

function CommandActions() {
  const navigate = useNavigate()
  const [receipts, setReceipts] = useState<Array<Record<string, unknown>> | null>(null)
  const [blockedJobs, setBlockedJobs] = useState<Array<Record<string, unknown>> | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const result = await apiRequest<{ receipts?: Array<Record<string, unknown>> }>(
          '/api/square/command-center/receipts', { method: 'GET' },
        )
        if (!cancelled) setReceipts((result.receipts ?? []).slice(0, 5))
      } catch {
        if (!cancelled) setReceipts([])
      }
      try {
        const result = await apiRequest<{ jobs?: Array<Record<string, unknown>> }>(
          '/api/square/command-center/jobs', { method: 'GET', query: { state: 'blocked' } },
        )
        if (!cancelled) setBlockedJobs((result.jobs ?? []).slice(0, 5))
      } catch {
        if (!cancelled) setBlockedJobs([])
      }
    })()
    return () => { cancelled = true }
  }, [])

  const rowStyle = {
    fontSize: 11,
    color: SHELL_TOKENS.inkDim,
    fontFamily: 'var(--font-mono)',
    padding: '6px 0',
    borderBottom: `1px solid ${SHELL_TOKENS.border}`,
  } as const

  return (
    <div>
      {sectionHeader('QUICK CREATE')}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
        {QUICK_CREATE.map((q) => (
          <button
            key={q.label}
            type="button"
            onClick={() => navigate(q.route)}
            style={{
              border: `1px solid ${SHELL_TOKENS.brandLine}`,
              background: `${SHELL_TOKENS.brand}14`,
              color: SHELL_TOKENS.ink,
              borderRadius: 7,
              padding: '7px 11px',
              cursor: 'pointer',
              fontSize: 11.5,
              fontWeight: 600,
              fontFamily: 'inherit',
            }}
          >
            {q.label}
          </button>
        ))}
      </div>

      {sectionHeader('NEEDS ATTENTION')}
      {blockedJobs === null && <div style={rowStyle}>Checking blocked jobs…</div>}
      {blockedJobs !== null && blockedJobs.length === 0 && (
        <div style={rowStyle}>No blocked jobs right now — nothing is waiting on you.</div>
      )}
      {(blockedJobs ?? []).map((j) => (
        <div key={String(j.id)} style={rowStyle}>
          ⚑ {(j.title as string) ?? 'Job'} · blocked
        </div>
      ))}

      {sectionHeader('RECENT ACTIVITY')}
      {receipts === null && <div style={rowStyle}>Loading recent receipts…</div>}
      {receipts !== null && receipts.length === 0 && (
        <div style={rowStyle}>No receipts yet — activity lands here as work is receipted.</div>
      )}
      {(receipts ?? []).map((r, i) => (
        <div key={String(r.id ?? i)} style={rowStyle}>
          {(r.receipt_type as string) ?? (r.action as string) ?? 'receipt'}
          {typeof r.summary === 'string' && r.summary ? ` · ${r.summary.slice(0, 60)}` : ''}
        </div>
      ))}

      <div style={{ fontSize: 10, color: SHELL_TOKENS.inkWhisper, marginTop: 12, lineHeight: 1.5 }}>
        Select any record anywhere in the shell to replace this with its full detail.
      </div>
    </div>
  )
}
